import {
  ALL_TILESET_KEYS,
  CLIFF_WINDOWS,
  SHADOW_ROWS,
  shadowAlpha,
  isCliffTileset,
  type AssetManifest,
  terrainFieldOf,
  TILE_SIZE,
  type ZoneDefinition,
} from '@shefira/shared';
import type Phaser from 'phaser';

/**
 * Draw a zone's ground as a tilemap layer from its Wang tilesets.
 *
 * Wang tiles are chosen by their four CORNERS (`TerrainField`): each tileset
 * draws the meeting of two terrains, so a tile is "which tileset, which
 * corner mask" — an index into that tileset's 4×4 grid image (build:atlas).
 * A tilemap rather than one texture because the open world is 256 tiles a
 * side: Phaser culls a layer to the camera for free, where a single
 * RenderTexture would be 4 000 px square.
 */
export interface TerrainOptions {
  scene: Phaser.Scene;
  zone: ZoneDefinition;
  manifest: AssetManifest;
}

/** Tiles per tileset grid: the sixteen corner masks, or one per cliff window (build:cliffs). */
const tilesPerSet = (key: string): number => (isCliffTileset(key) ? CLIFF_WINDOWS.length : 16);
/** Every corner the upper terrain: in the first tileset, plain grass. */
const ALL_UPPER = 15;

export function drawTerrain({
  scene,
  zone,
  manifest,
}: TerrainOptions): Phaser.Tilemaps.TilemapLayer | null {
  const map = scene.make.tilemap({
    tileWidth: TILE_SIZE,
    tileHeight: TILE_SIZE,
    width: zone.width,
    height: zone.height,
  });

  // One tileset image per terrain pair and per cliff (ADR 0020), each with
  // its own run of ids.
  const firstIdOf = new Map<string, number>();
  const tilesets: Phaser.Tilemaps.Tileset[] = [];
  let nextId = 1;
  for (const key of ALL_TILESET_KEYS) {
    if (manifest.tilesets[key] === undefined || !scene.textures.exists(key)) continue;
    const tileset = map.addTilesetImage(key, key, TILE_SIZE, TILE_SIZE, 0, 0, nextId);
    if (tileset === null) continue;
    tilesets.push(tileset);
    firstIdOf.set(key, nextId);
    nextId += tilesPerSet(key);
  }
  const fallbackId = firstIdOf.values().next().value;
  if (tilesets.length === 0 || fallbackId === undefined) return null;

  const ground = groundOf(zone);
  const ids: number[][] = [];
  for (let y = 0; y < zone.height; y += 1) {
    const row: number[] = [];
    for (let x = 0; x < zone.width; x += 1) {
      const at = y * zone.width + x;
      const firstId = firstIdOf.get(ground.tilesets[ground.tilesetAt[at] ?? -1] ?? '');
      // A conflict or a missing tileset shows as plain grass rather than a hole.
      row.push(firstId === undefined ? fallbackId + ALL_UPPER : firstId + (ground.masks[at] ?? 0));
    }
    ids.push(row);
  }

  const layer = map.createBlankLayer('ground', tilesets, 0, 0);
  if (layer === null) return null;
  map.putTilesAt(ids, 0, 0, false, layer);
  layer.setDepth(-1000);
  drawShadows(scene, ground.shadows);
  // Trees overhang their tiles by two rows; keep the ground under them drawn.
  layer.setCullPadding(2, 3);
  return layer;
}

/** Above the ground, under ground cover and anything that walks. */
const SHADOW_DEPTH = -900;
const SHADOW_KEY = 'relief-shadow';

/**
 * The shadow each south face casts on the ground below it (ADR 0020): one
 * image a column, `SHADOW_ROWS` tall, black fading down in steps of four pixels.
 * Its own texture, drawn once: a tint could only darken a tile whole, and
 * whole tiles read as squares, not as shade.
 */
function drawShadows(scene: Phaser.Scene, shadows: readonly Shadow[]): void {
  // Four textures: the middle of a face, its west end, its east end, a face one tile wide.
  const keyOf = (west: boolean, east: boolean): string =>
    `${SHADOW_KEY}-${west ? 'w' : ''}${east ? 'e' : ''}`;
  for (const west of [false, true]) {
    for (const east of [false, true]) {
      const key = keyOf(west, east);
      if (scene.textures.exists(key)) continue;
      const canvas = scene.textures.createCanvas(key, TILE_SIZE, SHADOW_ROWS);
      const context = canvas?.getContext();
      if (canvas === null || context === undefined) continue;
      for (let row = 0; row < SHADOW_ROWS; row += 1) {
        for (let column = 0; column < TILE_SIZE; column += 1) {
          context.fillStyle = `rgba(0, 0, 0, ${String(shadowAlpha(row, column, { west, east }))})`;
          context.fillRect(column, row, 1, 1);
        }
      }
      canvas.refresh();
    }
  }
  for (const { x, y, west, east } of shadows) {
    scene.add
      .image(x * TILE_SIZE, y * TILE_SIZE, keyOf(west, east))
      .setOrigin(0, 0)
      .setDepth(SHADOW_DEPTH);
  }
}

interface Shadow {
  x: number;
  y: number;
  west: boolean;
  east: boolean;
}

/** A zone's tiles as the Wang field chose them: an index into `tilesets` and a mask per tile. */
interface GroundChoice {
  tilesets: string[];
  tilesetAt: Int16Array;
  masks: Uint16Array;
  shadows: Shadow[];
}

/**
 * Choosing every tile of the world walks its corners and relief: a few
 * hundred milliseconds that each return from a cave paid again. A zone's
 * ground never changes while the page lives, so it is chosen once.
 */
const GROUND = new WeakMap<ZoneDefinition, GroundChoice>();

function groundOf(zone: ZoneDefinition): GroundChoice {
  const known = GROUND.get(zone);
  if (known !== undefined) return known;
  const field = terrainFieldOf(zone);
  const tilesets: string[] = [];
  const indexOf = new Map<string, number>();
  const tilesetAt = new Int16Array(zone.width * zone.height).fill(-1);
  const masks = new Uint16Array(zone.width * zone.height);
  const shadows: Shadow[] = [];
  for (let y = 0; y < zone.height; y += 1) {
    for (let x = 0; x < zone.width; x += 1) {
      const at = y * zone.width + x;
      const choice = field.tileAt(x, y);
      if (choice !== null) {
        let index = indexOf.get(choice.tileset);
        if (index === undefined) {
          index = tilesets.length;
          tilesets.push(choice.tileset);
          indexOf.set(choice.tileset, index);
        }
        tilesetAt[at] = index;
        masks[at] = choice.mask;
      }
      if (field.castsShadowAt(x, y)) shadows.push({ x, y, ...field.shadowEnds(x, y) });
    }
  }
  const ground = { tilesets, tilesetAt, masks, shadows };
  GROUND.set(zone, ground);
  return ground;
}
